/**
 * Welcome Popup Component
 *
 * Shows a welcome modal with tutorial video for first-time Bridge Club members.
 * Video: https://youtu.be/88EXMrIrur0
 *
 * Branches into TWO content variants based on member.is_local_mill_member
 * (computed server-side from local_access + home_location):
 *   - Variant A (retail partner): existing Buy-10-Get-1-Free copy
 *   - Variant B (local mill / Kettle Falls): Order Online + locker
 *     pickup + $10 store credit + BYOC, plus the shared benefits
 *
 * Shell (title, video, action buttons, dismiss tracking) is identical
 * across variants.
 */

import React, { useState } from 'react';
import { bridgeClubAPI } from '../../services/bridgeClubAPI';
import { useBridgeClubAuth } from '../../contexts/BridgeClubAuthContext';
import './WelcomePopup.css';

interface WelcomePopupProps {
  isOpen: boolean;
  onClose: (markAsSeen: boolean) => void;
}

// Shared benefits — appear in BOTH variants (different ordering /
// position, but same wording). Extracted so the two variant components
// stay readable and the wording stays consistent.
const SharedFeatures: React.FC = () => (
  <>
    <li><span className="feature-check">&#10003;</span> <strong>Track Feed Levels</strong> &mdash; Know when you're running low</li>
    <li><span className="feature-check">&#10003;</span> <strong>Manage Your Animals</strong> &mdash; Smart feed calculations that adjust as they grow</li>
    <li><span className="feature-check">&#10003;</span> <strong>Plan Store Trips</strong> &mdash; Calculate exactly how much feed you need</li>
    <li><span className="feature-check">&#10003;</span> <strong>Weather Alerts</strong> &mdash; Animal-specific guidance when dangerous weather is coming</li>
    <li><span className="feature-check">&#10003;</span> <strong>Health Tools</strong> &mdash; Diagnostic tools to troubleshoot issues</li>
    <li><span className="feature-check">&#10003;</span> <strong>Scan Receipts</strong> &mdash; Snap photos to log purchases and earn rewards</li>
    <li><span className="feature-check">&#10003;</span> <strong>Farm Challenge</strong> &mdash; Earn points that convert to cash back on feed!</li>
  </>
);

// Variant A — Retail Partner Bridge Club (existing copy, unchanged).
// The "Earn Free Feed" benefit is retail-partner-only (Buy 10 Get 1
// Free is the retail-partner loyalty program; local mill members get
// wholesale pricing direct so they don't qualify).
const RetailPartnerVariant: React.FC = () => (
  <div className="welcome-description">
    <p className="welcome-intro">
      Bridge Club is your free farm companion app &mdash; designed to help you track your animals,
      manage your feed, earn rewards, and stay connected with your local feed store.
    </p>
    <ul className="welcome-features">
      <li><span className="feature-check">&#10003;</span> <strong>Earn Free Feed</strong> &mdash; Buy 10 Get 1 Free, automatically tracked</li>
      <SharedFeatures />
    </ul>
  </div>
);

// Variant B — Local Mill Member (Kettle Falls).
// Unique-to-local benefits listed first so they're the most visible:
// Order Online, 24/7 Locker, $10 Store Credit (the local-points
// economy), BYOC. Then the shared feed/animals/weather/etc. The
// retail-partner Buy-10-Get-1-Free is intentionally absent — local
// members don't get it (they already get wholesale pricing).
const LocalMillVariant: React.FC = () => (
  <div className="welcome-description">
    <p className="welcome-intro">
      Bridge Club is your free farm companion app &mdash; designed to help you track your animals,
      manage your feed, earn rewards, and stay connected with Red Bridge Farm Feed Co.
    </p>
    <ul className="welcome-features">
      <li><span className="feature-check">&#10003;</span> <strong>Order Online</strong> &mdash; Place orders for in-store or locker pickup, anytime</li>
      <li><span className="feature-check">&#10003;</span> <strong>24/7 Locker Pickup</strong> &mdash; Grab your order from secure lockers on your schedule</li>
      <li><span className="feature-check">&#10003;</span> <strong>Earn $10 Store Credit</strong> &mdash; $1 spent = 1 point. 500 points = $10</li>
      <li><span className="feature-check">&#10003;</span> <strong>Bring Your Own Container (BYOC)</strong> &mdash; Save on packaging, track your bins in the app</li>
      <SharedFeatures />
    </ul>
  </div>
);

const WelcomePopup: React.FC<WelcomePopupProps> = ({ isOpen, onClose }) => {
  const [closing, setClosing] = useState(false);
  const { member } = useBridgeClubAuth();
  // is_local_mill_member is server-computed from local_access +
  // home_location. Treat undefined as false so members fetched
  // before the field was added still see the safe default (Variant A).
  const isLocalMill = member?.is_local_mill_member === true;

  const handleGetStarted = async () => {
    setClosing(true);
    try {
      await bridgeClubAPI.markWelcomeSeen();
    } catch (err) {
      console.error('Failed to mark welcome as seen:', err);
    }
    onClose(true);
  };

  const handleWatchLater = () => {
    // Don't mark as seen - will show again next login
    onClose(false);
  };

  const handleCloseButton = async () => {
    // Treat X button same as Get Started
    await handleGetStarted();
  };

  if (!isOpen) return null;

  return (
    <div className="welcome-overlay" onClick={handleCloseButton}>
      <div className="welcome-modal" onClick={(e) => e.stopPropagation()}>
        {/* Close Button */}
        <button
          className="welcome-close"
          onClick={handleCloseButton}
          aria-label="Close"
          disabled={closing}
        >
          &times;
        </button>

        {/* Title */}
        <h2 className="welcome-title">Welcome to Bridge Club!</h2>

        {/* Video Embed — same video for both variants. If a
            local-specific video is provided later, swap the URL
            inside the isLocalMill branch only. */}
        <div className="welcome-video-container">
          <iframe
            src="https://www.youtube.com/embed/88EXMrIrur0"
            title="Bridge Club Tutorial"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>

        {/* Content variant — driven by server-computed
            is_local_mill_member. Defaults to Variant A on undefined. */}
        {isLocalMill ? <LocalMillVariant /> : <RetailPartnerVariant />}

        {/* Action Buttons */}
        <div className="welcome-actions">
          <button
            className="welcome-btn welcome-btn-secondary"
            onClick={handleWatchLater}
            disabled={closing}
          >
            Watch Later
          </button>
          <button
            className="welcome-btn welcome-btn-primary"
            onClick={handleGetStarted}
            disabled={closing}
          >
            {closing ? 'Starting...' : 'Get Started'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WelcomePopup;
